import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';

interface Account {
  id: string;
  name: string;
  type: string;
}

interface ImportResult {
  importId: string;
  totalRows: number;
  newTransactions: number;
  duplicateSkipped: number;
  errors: string[];
  reviewItems: Array<{ id: string; type: string; description: string }>;
}

export default function ImportPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [mode, setMode] = useState<'append' | 'replace'>('append');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<Account[]>('/api/accounts').then((data) => {
      setAccounts(data);
      if (data.length > 0 && !selectedAccount) {
        setSelectedAccount(data[0].id);
      }
    });
  }, []);

  const handleUpload = useCallback(async () => {
    if (!file || !selectedAccount) return;

    setUploading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('accountId', selectedAccount);
      formData.append('mode', mode);

      const data = await api.upload<ImportResult>('/api/import/op-csv', formData);
      setResult(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }, [file, selectedAccount, mode]);

  const uncategorizedCount = result?.reviewItems.filter((i) => i.type === 'uncategorized').length ?? 0;

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Tuo OP CSV
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControl fullWidth>
              <InputLabel>Lähdetili</InputLabel>
              <Select
                value={selectedAccount}
                label="Lähdetili"
                onChange={(e) => setSelectedAccount(e.target.value)}
              >
                {accounts.map((acc) => (
                  <MenuItem key={acc.id} value={acc.id}>
                    {acc.name} ({acc.type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Tuontitapa</InputLabel>
              <Select
                value={mode}
                label="Tuontitapa"
                onChange={(e) => setMode(e.target.value as 'append' | 'replace')}
              >
                <MenuItem value="append">Lisää (yhdistä, ohita duplikaatit)</MenuItem>
                <MenuItem value="replace">Korvaa (ylikirjoita kuukauden tiedot)</MenuItem>
              </Select>
            </FormControl>

            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              sx={{ textAlign: 'left' }}
            >
              {file ? file.name : 'Valitse CSV-tiedosto'}
              <input
                type="file"
                hidden
                accept=".csv"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </Button>

            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!file || !selectedAccount || uploading}
              size="large"
            >
              {uploading ? 'Tuodaan...' : 'Tuo'}
            </Button>

            {uploading && <LinearProgress />}
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {result && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Tuonti valmis
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
              <Chip label={`${result.totalRows} riviä luettu`} color="primary" />
              <Chip label={`${result.newTransactions} uutta`} color="success" />
              <Chip label={`${result.duplicateSkipped} duplikaattia`} color="default" />
            </Box>

            {result.errors.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {result.errors.length} jäsennysvirhettä:
                <List dense>
                  {result.errors.slice(0, 5).map((err, i) => (
                    <ListItem key={i}>
                      <ListItemText primary={err} />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}

            {uncategorizedCount > 0 && (
              <Alert
                severity="warning"
                sx={{ mb: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => navigate('/review')}
                  >
                    Avaa tarkistettavat
                  </Button>
                }
              >
                {uncategorizedCount} tapahtumaa ilman luokittelua. Siirry Tarkistettavat-sivulle
                luodaksesi säännöt.
              </Alert>
            )}

            {uncategorizedCount === 0 && result.reviewItems.length === 0 && (
              <Alert severity="success">
                Kaikki tapahtumat luokiteltu automaattisesti sääntöjen perusteella!
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
